'use client';
import { createContext, useContext, useState, ReactNode } from 'react';
import { setCookie } from 'cookies-next';
import {
  DEFAULT_MODELS,
  DEFAULT_IRS,
  DEFAULT_INPUTS,
  NO_IR,
  IR,
  Model,
  Input,
} from 'neural-amp-modeler-wasm';
import { PLAYER_CONFIG_KEY } from '@/utils/player/config-key';

type PlayerConfig = {
  inputs: {
    guitar: Input;
    bass: Input;
  };
  ampIrs: {
    guitar: IR;
    bass: IR;
  };
  pedalIrs: {
    guitar: IR;
    bass: IR;
  };
  models: {
    guitar: Model;
    bass: Model;
  };
};

// Default configuration
export const DEFAULT_CONFIG: PlayerConfig = {
  inputs: {
    guitar: DEFAULT_INPUTS.find((sample) => sample.name === 'Mayer - Guitar')!,
    bass: DEFAULT_INPUTS.find((sample) => sample.name === 'Garden - Bass')!,
  },
  ampIrs: {
    guitar: DEFAULT_IRS.find((sample) => sample.name === 'Mesa 412 OS')!,
    bass: DEFAULT_IRS.find((sample) => sample.name === 'Ampeg 8x10')!,
  },
  pedalIrs: {
    guitar: NO_IR,
    bass: NO_IR,
  },
  models: {
    guitar: DEFAULT_MODELS.find((sample) => sample.name === 'Fender Deluxe Reverb')!,
    bass: DEFAULT_MODELS.find((sample) => sample.name === 'Ampeg V-2')!,
  },
};

type PlayerConfigContextType = {
  config: PlayerConfig;
  updateConfig: (newConfig: Partial<PlayerConfig>) => void;
};

const PlayerConfigContext = createContext<PlayerConfigContextType | undefined>(undefined);

// Save config to cookies
const saveConfigToCookies = (config: PlayerConfig) => {
  try {
    setCookie(PLAYER_CONFIG_KEY, JSON.stringify(config), {
      maxAge: 60 * 60 * 24 * 365, // 1 year
      sameSite: 'lax',
      secure: false,
    });
  } catch (e) {
    console.error('Failed to save player config to cookies:', e);
  }
};

const getConfigFromCookie = (cookie: string | undefined): PlayerConfig => {
  try {
    if (!cookie) {
      return DEFAULT_CONFIG;
    }
    // Cookies written before a field existed lack it; `irs` is the old `ampIrs`.
    const { irs, ...saved } = JSON.parse(cookie);
    return { ...DEFAULT_CONFIG, ...(irs && { ampIrs: irs }), ...saved };
  } catch (e) {
    console.error('Failed to get config from cookie:', e);
    return DEFAULT_CONFIG;
  }
};

export function PlayerConfigProvider({
  cookie,
  persist = true,
  children,
}: {
  cookie: string | undefined;
  /**
   * Write changes back to the cookie. Off inside embedded players on other
   * sites: third-party cookies are blocked or partitioned there, and the
   * embed route is served without reading cookies anyway, so the choice
   * lives for the page only.
   */
  persist?: boolean;
  children: ReactNode;
}) {
  const [config, setConfig] = useState<PlayerConfig>(getConfigFromCookie(cookie));

  const updateConfig = (newConfig: Partial<PlayerConfig>) => {
    const updatedConfig = { ...config, ...newConfig };
    setConfig(updatedConfig);
    if (persist) saveConfigToCookies(updatedConfig);
  };

  return (
    <PlayerConfigContext.Provider value={{ config, updateConfig }}>
      {children}
    </PlayerConfigContext.Provider>
  );
}

export function usePlayerConfig() {
  const context = useContext(PlayerConfigContext);
  if (context === undefined) {
    throw new Error('usePlayerConfig must be used within a PlayerConfigProvider');
  }
  return context;
}
